/**
 * Cart Gift Sync - Global handler for free gift synchronization
 * This ensures gift eligibility is checked across both cart drawer and cart page
 */
class CartGiftSync {
  constructor() {
    // Exit immediately if on cart page - don't even set up properties
    if (window.location.pathname === '/cart') {
      return;
    }
    
    this.isProcessing = false;
    this.lastRefreshTime = 0; // Track last refresh to prevent rapid refreshes
    this.init();
  }

  /**
   * Get gift threshold dynamically from cart drawer or fallback
   */
  getGiftThreshold() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const thresholdFromElement = cartDrawer.getAttribute('data-gift-threshold');
      if (thresholdFromElement) {
        return parseInt(thresholdFromElement) * 100; // Convert dollars to cents
      }
    }
    return 15000; // Fallback to $150.00 in cents
  }

  init() {
    // Only initialize if we're NOT on the cart page
    if (window.location.pathname === '/cart') {
      return; // Exit immediately if on cart page
    }

    // Check gift eligibility on page load
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        this.checkGiftEligibilityGlobal();
      }, 1000);
    });

    // Listen for cart changes but with more intelligent handling
    document.addEventListener('on:cart:change', () => {
      // Skip if on cart page
      if (window.location.pathname === '/cart') return;
      
      // Use shorter delay and be smarter about when to check
      setTimeout(() => {
        this.checkGiftEligibilityGlobal();
      }, 300); // Reduced delay for faster response to removals
    });

    // Listen for when products are added to cart (more immediate than cart:change)
    document.addEventListener('on:cart:add', () => {
      // Skip if on cart page
      if (window.location.pathname === '/cart') return;
      
      // Check immediately when product is added - reduced delay for faster response
      setTimeout(() => {
        this.checkGiftEligibilityGlobal();
      }, 100);
    });

    // Listen for page visibility changes (when user switches tabs/windows)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && window.location.pathname !== '/cart') {
        setTimeout(() => {
          this.checkGiftEligibilityGlobal();
        }, 500);
      }
    });

    // Listen for cart drawer close events specifically
    document.addEventListener('dispatch:cart-drawer:close', () => {
      // Skip if on cart page
      if (window.location.pathname === '/cart') return;
      
      setTimeout(() => {
        this.checkGiftEligibilityGlobal();
      }, 300);
    });
  }

  /**
   * Global gift eligibility check
   * Only operates on cart drawer, never on cart page to prevent conflicts
   */
  async checkGiftEligibilityGlobal() {
    // NEVER operate on cart page - let cart page handle its own logic
    if (window.location.pathname === '/cart') {
      return;
    }

    if (this.isProcessing) {
      return;
    }

    try {
      this.isProcessing = true;
      const response = await fetch('/cart.json');
      const cart = await response.json();
      
      // Get threshold dynamically each time to ensure consistency
      const giftThreshold = this.getGiftThreshold();
      
      const hasGiftProduct = cart.items.some(item => 
        item.properties && item.properties._gift_item === 'true'
      );

      // Calculate subtotal without gift items
      const subtotalWithoutGifts = cart.items
        .filter(item => !item.properties || item.properties._gift_item !== 'true')
        .reduce((total, item) => total + item.final_line_price, 0);

      if (subtotalWithoutGifts < giftThreshold && hasGiftProduct) {
        // Remove gift if below threshold
        
        // Hide gift containers in UI immediately
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const giftContainers = cartDrawer.querySelectorAll('.cart-gift-item');
          giftContainers.forEach(container => {
            container.style.display = 'none';
          });
        }
        
        await this.removeGiftItems(cart);
      } else if (subtotalWithoutGifts >= giftThreshold && !hasGiftProduct) {
        // Cart qualifies but no gift - refresh display
        // Be less restrictive for recent cart additions
        const now = Date.now();
        const timeSinceLastRefresh = now - this.lastRefreshTime;
        
        // If it's been more than 3 seconds, or if this is a fresh add-to-cart, refresh
        if (timeSinceLastRefresh > 3000) {
          this.lastRefreshTime = now;
          this.refreshCartDisplay();
        }
      } else if (subtotalWithoutGifts < giftThreshold && !hasGiftProduct) {
        // Cart doesn't qualify and no gift present - make sure gift container is hidden
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const giftContainers = cartDrawer.querySelectorAll('.cart-gift-item');
          if (giftContainers.length > 0) {
            giftContainers.forEach(container => {
              container.style.display = 'none';
              // Remove completely to clean up
              container.remove();
            });
          }
        }
      }
    } catch (error) {
      console.error('Error in global gift eligibility check:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Refresh cart display to show/hide gift options
   * ONLY affects cart drawer, never the cart page
   */
  refreshCartDisplay() {
    // Only refresh cart drawer, never interfere with cart page
    if (window.location.pathname === '/cart') {
      return; // Do nothing on cart page
    }
    
    // If cart drawer is open, refresh it completely
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      
      // Try the new complete refresh method first
      if (cartDrawer.refreshEntireSection) {
        cartDrawer.refreshEntireSection();
        return;
      }
      
      // Fallback to cart form refresh
      const cartForm = cartDrawer.querySelector('cart-form');
      if (cartForm && cartForm.refresh) {
        cartForm.refresh();
        return;
      }
    }
    
    // Force a manual cart drawer refresh using event dispatch
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
      bubbles: true,
      cancelable: false
    }));
  }

  /**
   * Remove gift items from cart
   * Only handles cart drawer updates, cart page handles its own updates
   */
  async removeGiftItems(cart) {
    const giftItems = cart.items.filter(item => 
      item.properties && item.properties._gift_item === 'true'
    );

    if (giftItems.length > 0) {
      try {
        
        const removePromises = giftItems.map(giftItem => 
          fetch('/cart/change.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: giftItem.key,
              quantity: 0
            })
          })
        );

        await Promise.all(removePromises);
        
        // If we're on the cart page, do nothing - let cart page handle its own updates
        if (window.location.pathname === '/cart') {
          return; // Cart page will handle its own refresh
        } else {
          // For cart drawer, just trigger a refresh without interfering with normal operations
          setTimeout(() => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.querySelector('cart-form')) {
              cartDrawer.querySelector('cart-form').refresh();
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error removing gift items:', error);
        // Only reload cart drawer, never the cart page
        if (window.location.pathname !== '/cart') {
          setTimeout(() => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.querySelector('cart-form')) {
              cartDrawer.querySelector('cart-form').refresh();
            }
          }, 1000);
        }
      }
    }
  }
}

// Initialize the global cart gift sync only if NOT on cart page
// Additional check to prevent any execution on cart page
if (window.location.pathname !== '/cart' && !window.location.pathname.includes('/cart')) {
  window.cartGiftSync = new CartGiftSync();
} else {
  // Explicitly set to null on cart page to prevent any accidental usage
  window.cartGiftSync = null;
}
